import { useQuery } from "@tanstack/react-query"
import { AlertCircle, ChevronLeft, ChevronRight } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { consultaDeApi, type Filtros } from "@/componentes/tareas/FiltrosTareas"
import type { Tarea } from "@/componentes/tareas/TarjetaTarea"

/**
 * El mes, con lo que vence cada día.
 *
 * ⚠ **Contesta una pregunta que el tablero no puede contestar: «¿qué semana está
 * cargada?».** El tablero ordena por estado y la lista por vencimiento, y las dos
 * enseñan las fechas de una en una; ninguna deja ver que el jueves hay seis cosas y
 * el viernes ninguna, que es lo único que permite mover algo ANTES de que se acumule.
 *
 * ⚠ **Sólo lo que tiene fecha.** Una tarea sin vencimiento no cae en ningún día, y
 * colarla en el mes en curso la pintaría como si venciera hoy. Se dice en el pie para
 * que su ausencia no se lea como que no existe.
 *
 * ⚠ **El recorte lo hace el servidor** —`vence_desde` y `vence_hasta`—, no un filtro
 * sobre todo lo traído. Con el filtro en el navegador, un mes con más tareas que el
 * límite de página enseñaría un calendario incompleto sin decirlo.
 *
 * ⚠ **La semana empieza el lunes.** Con el domingo delante, el fin de semana queda
 * partido entre la primera y la última columna y las cinco casillas que importan
 * dejan de leerse juntas.
 *
 * ⚠ **Nada se anima al refrescar.** El sondeo de 60 s haría parpadear el mes entero
 * cada minuto.
 *
 * ⚠ **Cada tarea es una ficha con fondo y borde, no una línea de texto.** Nació
 * como texto sobre la casilla y no se leía como algo pulsable: había que descubrir
 * a base de clics que abría la ficha. En una rejilla donde todo lo demás —números
 * de día, huecos— también es texto, lo que se puede tocar tiene que verse distinto
 * antes de tocarlo, no después.
 */

interface Pagina {
  total: number
  items: Tarea[]
}

/**
 * La barra de color del borde izquierdo de cada ficha.
 *
 * ⚠ **`undefined` para MEDIA y BAJA, no un gris.** Es el mismo reparto que la
 * tarjeta del tablero: sólo `URGENTE` y `ALTA` llevan color. En una rejilla de
 * treinta y cinco casillas, teñir también lo normal deja el mes entero rayado y
 * entonces lo urgente ya no salta.
 */
const COLOR_PRIORIDAD: Partial<Record<Tarea["prioridad"], string>> = {
  URGENTE: "var(--destructive)",
  ALTA: "var(--warning)",
}

/** Cuántas caben en una casilla antes de resumir. Con más, la fila del mes crece
 *  hasta que el mes deja de verse de una vez, que es para lo único que sirve.
 *
 *  ⚠ **Dos en compacto y no tres.** Compartiendo la vista con los contadores y con
 *  la lista de lo próximo, el mes tiene la mitad del ancho: la tercera ficha ya no
 *  cabe sin que la casilla crezca, y una rejilla de treinta y cinco casillas altas
 *  empuja todo lo demás fuera de la pantalla. Se resume antes, y el «+N más» sigue
 *  diciendo lo que no se ve. */
const POR_DIA = 3
const POR_DIA_COMPACTO = 2

const DIAS = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sá", "Do"]

const MESES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
]

/** `YYYY-MM-DD` en hora LOCAL. `toISOString()` daría UTC y en Caracas —cuatro horas
 *  detrás— desplazaría un día entero cada tarde. */
function iso(d: Date): string {
  const mes = String(d.getMonth() + 1).padStart(2, "0")
  const dia = String(d.getDate()).padStart(2, "0")
  return `${d.getFullYear()}-${mes}-${dia}`
}

/** Lunes = 0. `getDay()` da domingo = 0, que es la convención que no usamos. */
function columna(d: Date): number {
  return (d.getDay() + 6) % 7
}

/** Las casillas del mes: los días del mes más los huecos de delante y de detrás. */
export function casillasDe(ancla: Date): (Date | null)[] {
  const primero = new Date(ancla.getFullYear(), ancla.getMonth(), 1)
  const ultimo = new Date(ancla.getFullYear(), ancla.getMonth() + 1, 0)
  const celdas: (Date | null)[] = Array(columna(primero)).fill(null)
  for (let d = 1; d <= ultimo.getDate(); d++) {
    celdas.push(new Date(ancla.getFullYear(), ancla.getMonth(), d))
  }
  // Se completa la última semana: sin esto la fila final queda con menos columnas y
  // la rejilla se descuadra visualmente.
  while (celdas.length % 7 !== 0) celdas.push(null)
  return celdas
}

type Props = {
  filtros: Filtros
  usuarioId: number | undefined
  onAbrir: (id: number) => void
  seleccionada: number | null
  /**
   * Para cuando el mes comparte la vista con otra cosa.
   *
   * ⚠ **Es una variante de densidad, no un componente distinto.** El mes entero a
   * pantalla completa era demasiado para lo que aporta —una pestaña propia para
   * mirar treinta y cinco casillas—, pero partirlo en dos componentes habría dejado
   * dos calendarios que se separan en cuanto uno se toca. Cambia cuántas fichas
   * caben y el alto de la casilla; el resto es el mismo código.
   */
  compacto?: boolean
}

export function CalendarioTareas({
  filtros,
  usuarioId,
  onAbrir,
  seleccionada,
  compacto = false,
}: Props) {
  const porDia = compacto ? POR_DIA_COMPACTO : POR_DIA
  const altoCasilla = compacto ? "min-h-20" : "min-h-28"
  const [ancla, setAncla] = useState(() => {
    const h = new Date()
    return new Date(h.getFullYear(), h.getMonth(), 1)
  })

  const desde = new Date(ancla.getFullYear(), ancla.getMonth(), 1)
  const hasta = new Date(ancla.getFullYear(), ancla.getMonth() + 1, 0)

  const params = consultaDeApi(filtros, usuarioId)
  params.set("vence_desde", iso(desde))
  params.set("vence_hasta", iso(hasta))

  const consulta = useQuery({
    // El mes va en la clave: sin él, pasar de página enseñaría el mes anterior
    // cacheado mientras carga el siguiente.
    queryKey: ["tareas", "calendario", filtros, iso(desde)],
    queryFn: () => api.get<Pagina>(`/api/v1/tareas?${params}`),
    refetchInterval: 60_000,
  })

  const agenda = new Map<string, Tarea[]>()
  for (const t of consulta.data?.items ?? []) {
    if (t.vence_en === null) continue
    const clave = t.vence_en.slice(0, 10)
    const cubo = agenda.get(clave)
    if (cubo === undefined) agenda.set(clave, [t])
    else cubo.push(t)
  }

  const hoy = iso(new Date())
  const mover = (meses: number) =>
    setAncla(new Date(ancla.getFullYear(), ancla.getMonth() + meses, 1))

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => mover(-1)}
          aria-label="Mes anterior"
          className="flex h-9 w-9 items-center justify-center rounded-[var(--radius-control)] border"
          style={{ borderColor: "var(--border)" }}
        >
          <ChevronLeft size={16} aria-hidden={true} />
        </button>
        <button
          type="button"
          onClick={() => mover(1)}
          aria-label="Mes siguiente"
          className="flex h-9 w-9 items-center justify-center rounded-[var(--radius-control)] border"
          style={{ borderColor: "var(--border)" }}
        >
          <ChevronRight size={16} aria-hidden={true} />
        </button>
        {/* `aria-live`: quien navega con lector de pantalla pulsa la flecha y nada
            en el foco cambia — sin esto, el mes cambia en silencio. */}
        <h2 className="text-sm font-medium tabular-nums" aria-live="polite">
          {MESES[ancla.getMonth()]} de {ancla.getFullYear()}
        </h2>
        {consulta.isFetching && (
          <span className="text-xs" style={{ color: "var(--fg-muted)" }}>
            actualizando…
          </span>
        )}
      </div>

      {consulta.isError && (
        <div
          role="alert"
          className="mb-3 flex items-start gap-2 text-sm"
          style={{ color: "var(--destructive)" }}
        >
          <AlertCircle size={16} aria-hidden={true} />
          {consulta.error instanceof ErrorApi ? consulta.error.message : "No se pudo cargar el mes"}
        </div>
      )}

      <div
        className="grid grid-cols-7 gap-px overflow-hidden rounded-[var(--radius-tarjeta)] border"
        style={{ borderColor: "var(--border)", backgroundColor: "var(--border)" }}
      >
        {DIAS.map((d) => (
          <div
            key={d}
            className="py-1 text-center text-xs font-medium"
            style={{ backgroundColor: "var(--surface-elevated)", color: "var(--fg-muted)" }}
          >
            {d}
          </div>
        ))}

        {casillasDe(ancla).map((dia, i) => {
          if (dia === null) {
            return (
              <div
                // Los huecos no tienen fecha con la que identificarse, y su posición
                // ES su identidad: no se reordenan nunca.
                // biome-ignore lint/suspicious/noArrayIndexKey: un hueco no tiene id
                key={`hueco-${i}`}
                style={{ backgroundColor: "var(--surface-elevated)" }}
                className={altoCasilla}
              />
            )
          }
          const clave = iso(dia)
          const delDia = agenda.get(clave) ?? []
          const esHoy = clave === hoy
          return (
            <div
              key={clave}
              className={`${altoCasilla} p-1`}
              // Hoy se tiñe la casilla entera, no sólo su número: en una rejilla de
              // treinta y cinco huecos iguales, un número de otro color hay que
              // buscarlo, y una casilla de otro fondo se encuentra sola.
              style={{ backgroundColor: esHoy ? "var(--primary-suave)" : "var(--surface)" }}
            >
              <div
                className="mb-1 text-right text-xs tabular-nums"
                style={{
                  color: esHoy ? "var(--primary-texto)" : "var(--fg-muted)",
                  fontWeight: esHoy ? 600 : 400,
                }}
              >
                {dia.getDate()}
              </div>
              {delDia.slice(0, porDia).map((t) => {
                const elegida = t.id === seleccionada
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => onAbrir(t.id)}
                    title={`${t.codigo} — ${t.titulo}`}
                    data-testid={`calendario-tarea-${t.id}`}
                    className="ficha-calendario"
                    style={{
                      backgroundColor: elegida ? "var(--primary-suave)" : "var(--surface-elevated)",
                      borderColor: elegida ? "var(--primary-texto)" : "var(--border)",
                      // La barra de prioridad, y sólo para lo que corre prisa: si
                      // todo destaca, nada destaca.
                      borderLeftColor: COLOR_PRIORIDAD[t.prioridad] ?? "var(--border)",
                    }}
                  >
                    {t.titulo}
                  </button>
                )
              })}
              {delDia.length > porDia && (
                <span className="px-1 text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
                  +{delDia.length - porDia} más
                </span>
              )}
            </div>
          )
        })}
      </div>

      <p className="mt-2 text-xs" style={{ color: "var(--fg-muted)" }}>
        El calendario sólo enseña tareas con fecha de vencimiento.
      </p>
    </div>
  )
}
